import { useCallback, useMemo, useState } from "react";
import type { MediaAsset } from "@eaub/shared";
import { useApi } from "../../api/ApiProvider";
import { useAsync } from "../../lib/useAsync";
import { asString, asStringArray } from "../../lib/values";
import { MediaPicker } from "../MediaPicker";
import { Spinner } from "../Spinner";

interface MediaInputProps {
  id: string;
  multiple: boolean;
  value: unknown;
  onChange: (value: unknown) => void;
}

export function MediaInput({ id, multiple, value, onChange }: MediaInputProps) {
  const api = useApi();
  const load = useCallback(() => api.listMedia(), [api]);
  const { data, loading } = useAsync<MediaAsset[]>(load, [load]);
  const [picking, setPicking] = useState(false);

  const selectedIds = useMemo<string[]>(() => {
    if (multiple) return asStringArray(value);
    const single = asString(value);
    return single ? [single] : [];
  }, [multiple, value]);

  const byId = useMemo(() => {
    const map = new Map<string, MediaAsset>();
    for (const asset of data ?? []) map.set(asset.id, asset);
    return map;
  }, [data]);

  const remove = (removeId: string) => {
    if (multiple) {
      onChange(selectedIds.filter((v) => v !== removeId));
    } else {
      onChange(undefined);
    }
  };

  const onConfirm = (ids: string[]) => {
    onChange(multiple ? ids : (ids[0] ?? undefined));
    setPicking(false);
  };

  return (
    <div className="media-input">
      {selectedIds.length > 0 ? (
        <ul className="media-input__selected">
          {selectedIds.map((selectedId) => {
            const asset = byId.get(selectedId);
            const isImage = asset?.contentType.startsWith("image/") ?? false;
            return (
              <li key={selectedId} className="media-input__chip">
                <div className="media-input__thumb">
                  {asset && isImage ? (
                    <img src={asset.url} alt={asset.filename} loading="lazy" />
                  ) : (
                    <span className="media-input__placeholder" aria-hidden="true">
                      {loading ? "…" : asset ? asset.contentType : "missing"}
                    </span>
                  )}
                </div>
                <span className="media-input__filename" title={asset?.filename ?? selectedId}>
                  {asset?.filename ?? selectedId}
                </span>
                <button
                  type="button"
                  className="btn btn--ghost btn--xs"
                  onClick={() => remove(selectedId)}
                >
                  Remove
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted media-input__empty">
          {loading ? <Spinner inline label="Loading…" /> : "No media selected."}
        </p>
      )}

      <button
        type="button"
        id={id}
        className="btn btn--secondary btn--sm"
        onClick={() => setPicking(true)}
      >
        {selectedIds.length > 0 ? "Change selection" : "Select media"}
      </button>

      {picking ? (
        <MediaPicker
          multiple={multiple}
          initialSelected={selectedIds}
          onConfirm={onConfirm}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </div>
  );
}
